import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Plus, Edit3, Trash2, MessageCircle, Bot, Sparkles, Search, FileText, Cpu, Layers, Loader2 } from 'lucide-react';
import { ChatConversation } from '../services/sessionService';

interface ConversationSidebarProps {
  conversations: ChatConversation[];
  activeConversationId: string | null;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onSelectConversation: (conversationId: string) => void;
  onCreateConversation: () => void;
  onDeleteConversation: (conversationId: string) => void;
  onRenameConversation: (conversationId: string, newName: string) => void;
  onGenerateConversationName: (conversationId: string) => void;
  onDuplicateConversation?: (conversationId: string) => void;
  onTogglePinConversation?: (conversationId: string) => void;
  onBatchGenerate?: () => void;
  isGeneratingName?: string | null; // conversationId of conversation being renamed
  isReadOnly?: boolean;
  activeJobs?: Map<string, { jobId: string; status: 'pending' | 'running' }>; // Active async jobs per conversation
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  activeConversationId,
  isCollapsed,
  onToggleCollapse,
  onSelectConversation,
  onCreateConversation,
  onDeleteConversation,
  onRenameConversation,
  onGenerateConversationName,
  onDuplicateConversation,
  onTogglePinConversation,
  onBatchGenerate,
  isGeneratingName,
  isReadOnly = false,
  activeJobs = new Map()
}) => {
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleStartRename = (conversation: ChatConversation) => {
    setEditingConversationId(conversation.id);
    setEditingName(conversation.name);
  };

  const handleSaveRename = () => {
    if (editingConversationId && editingName.trim()) {
      onRenameConversation(editingConversationId, editingName.trim());
    }
    setEditingConversationId(null);
    setEditingName('');
  };

  const handleCancelRename = () => {
    setEditingConversationId(null);
    setEditingName('');
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return new Date(date).toLocaleDateString();
  };

  const getConversationPreview = (conversation: ChatConversation) => {
    const lastMessage = conversation.messages[conversation.messages.length - 1];
    if (!lastMessage) return 'No messages yet';
    
    const content = lastMessage.content;
    if (content.length > 50) {
      return content.substring(0, 50) + '...';
    }
    return content;
  };

  const getDateGroupName = (date: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const conversationDate = new Date(new Date(date).getFullYear(), new Date(date).getMonth(), new Date(date).getDate());

    if (conversationDate.getTime() === today.getTime()) {
      return 'Today';
    } else if (conversationDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    } else if (conversationDate >= weekAgo) {
      const dayName = conversationDate.toLocaleDateString('en-US', { weekday: 'long' });
      return dayName;
    } else if (conversationDate.getFullYear() === now.getFullYear()) {
      return conversationDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    } else {
      return conversationDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
    }
  };

  const renderSettingsIndicators = (conversation: ChatConversation) => {
    const settings = conversation.chatSettings;
    if (!settings) return null;

    const indicators = [];


    // Web Search indicator
    if (settings.enableWebSearch) {
      indicators.push(
        <div key="search" title="Web Search Enabled">
          <Search className="w-3 h-3 text-[#B57700]" />
        </div>
      );
    }

    // Uploaded Files indicator
    if (settings.uploadedFiles && settings.uploadedFiles.length > 0) {
      indicators.push(
        <div key="files" title={`${settings.uploadedFiles.length} Files Uploaded`}>
          <FileText className="w-3 h-3 text-[#232E3F] dark:text-[#FFCF74]" />
        </div>
      );
    }

    // Model indicator (show if not default)
    if (settings.selectedModel && settings.selectedModel !== 'gpt-4o') {
      indicators.push(
        <div key="model" title={`Model: ${settings.selectedModel}`}>
          <Cpu className="w-3 h-3 text-red-500" />
        </div>
      );
    }

    return indicators.length > 0 ? (
      <div className="flex items-center space-x-1">
        {indicators}
      </div>
    ) : null;
  };

  const groupConversationsByDate = (conversations: ChatConversation[]) => {
    // Sort by createdAt (not updatedAt) so conversations don't jump around when updated
    const sorted = conversations.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const groups: Record<string, ChatConversation[]> = {};

    sorted.forEach(conversation => {
      const groupName = getDateGroupName(conversation.createdAt);
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(conversation);
    });
    
    // Sort groups by priority (Today, Yesterday, etc.)
    const groupOrder = ['Today', 'Yesterday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      const aIndex = groupOrder.indexOf(a);
      const bIndex = groupOrder.indexOf(b);
      
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      } else if (aIndex !== -1) {
        return -1;
      } else if (bIndex !== -1) {
        return 1;
      } else {
        // For month/year groups, maintain reverse chronological order
        return b.localeCompare(a);
      }
    });
    
    return sortedGroups;
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
        <button
          onClick={onToggleCollapse}
          className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-200 dark:border-gray-700"
          title="Expand conversations"
        >
          <ChevronRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
        </button>
        
        {!isReadOnly && (
          <button
            onClick={onCreateConversation}
            className="p-3 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-primary-600 dark:text-primary-400"
            title="New conversation"
          >
            <Plus className="w-4 h-4" />
          </button>
        )}

        <div className="flex-1 flex flex-col space-y-1 px-1 py-2">
          {conversations.map((conversation) => (
            <button
              key={conversation.id}
              onClick={() => onSelectConversation(conversation.id)}
              className={`p-2 rounded transition-colors relative ${
                activeConversationId === conversation.id
                  ? 'bg-primary-100 dark:bg-primary-900'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              title={conversation.name}
            >
              <MessageCircle className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              {conversation.messages.length > 0 && (
                <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-[#FFB52B] text-xs"></div>
              )}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-80 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Conversations</h2>
          <div className="flex items-center space-x-2">
            {!isReadOnly && (
              <>
                <button
                  onClick={onCreateConversation}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors text-primary-600 dark:text-primary-400"
                  title="New conversation"
                >
                  <Plus className="w-4 h-4" />
                </button>
                {onBatchGenerate && (
                  <button
                    onClick={onBatchGenerate}
                    className="rounded p-2 text-[#232E3F] transition-colors hover:bg-gray-100 dark:text-[#FFCF74] dark:hover:bg-gray-700"
                    title="Batch generate conversations"
                  >
                    <Layers className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
            <button
              onClick={onToggleCollapse}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Collapse sidebar"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No conversations yet</p>
            {!isReadOnly && (
              <button
                onClick={onCreateConversation}
                className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Start your first conversation
              </button>
            )}
          </div>
        ) : (
          <div className="p-2 space-y-3">
            {groupConversationsByDate(conversations).map(([groupName, groupConversations]) => (
              <div key={groupName} className="space-y-1">
                <div className="px-2 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {groupName}
                </div>
                {groupConversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`group relative rounded-lg border transition-colors ${
                    activeConversationId === conversation.id
                      ? 'bg-primary-50 dark:bg-primary-800/30 border-primary-200 dark:border-primary-600'
                      : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
                  }`}
                >
                  {editingConversationId === conversation.id ? (
                    <div className="p-3">
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveRename();
                          if (e.key === 'Escape') handleCancelRename();
                        }}
                        onBlur={handleSaveRename}
                        className="w-full p-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                        autoFocus
                      />
                    </div>
                  ) : (
                    <button
                      onClick={() => onSelectConversation(conversation.id)}
                      className="w-full p-3 text-left bg-transparent border-none focus:outline-none focus:ring-0"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h3 className={`text-sm font-medium truncate ${
                              activeConversationId === conversation.id
                                ? 'text-gray-900 dark:text-white'
                                : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {conversation.name}
                            </h3>
                            {activeJobs.has(conversation.id) && (
                              <div className="flex-shrink-0" title={`Processing${activeJobs.get(conversation.id)?.status === 'pending' ? ' (queued)' : ''}...`}>
                                <Loader2 className="h-3 w-3 animate-spin text-[#FFB52B]" />
                              </div>
                            )}
                            <div className="flex-shrink-0">
                              {renderSettingsIndicators(conversation)}
                            </div>
                          </div>
                          <p className={`text-xs mt-1 truncate ${
                            activeConversationId === conversation.id
                              ? 'text-gray-600 dark:text-gray-100'
                              : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {getConversationPreview(conversation)}
                          </p>
                          <div className="flex items-center justify-between mt-2">
                            <span className={`text-xs ${
                              activeConversationId === conversation.id
                                ? 'text-gray-500 dark:text-gray-200'
                                : 'text-gray-400 dark:text-gray-500'
                            }`}>
                              {formatTimeAgo(conversation.updatedAt)}
                            </span>
                            <span className={`text-xs ${
                              activeConversationId === conversation.id
                                ? 'text-gray-500 dark:text-gray-200'
                                : 'text-gray-400 dark:text-gray-500'
                            }`}>
                              {conversation.messages.length} messages
                            </span>
                          </div>
                        </div>
                      </div>
                    </button>
                  )}

                  {/* Action buttons - show on hover (disabled for read-only access) */}
                  {editingConversationId !== conversation.id && !isReadOnly && (
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onGenerateConversationName(conversation.id);
                        }}
                        disabled={isGeneratingName === conversation.id}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                        title="Generate AI name"
                      >
                        {isGeneratingName === conversation.id ? (
                          <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <Sparkles className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartRename(conversation);
                        }}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                        title="Rename conversation"
                      >
                        <Edit3 className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const confirmMsg = conversations.length === 1
                            ? 'Delete this conversation? A new empty conversation will be created.'
                            : 'Delete this conversation? This action cannot be undone.';
                          if (window.confirm(confirmMsg)) {
                            onDeleteConversation(conversation.id);
                          }
                        }}
                        className="p-1 hover:bg-red-200 dark:hover:bg-red-900 rounded transition-colors"
                        title="Delete conversation"
                      >
                        <Trash2 className="w-3 h-3 text-red-500 dark:text-red-400" />
                      </button>
                    </div>
                  )}
                </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationSidebar;
